import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, Star, Target, Brain, BookOpen, MessageSquare, Sparkles,
  ChevronRight, Flame, Award, Users, CheckCircle, Zap, MapPin,
  Calendar, HelpCircle, ArrowRight, LogIn, FolderOpen, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const LOGO = '/yas-logo.png';

const SYEPLaunchPage = () => {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState('youth');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      
      {/* Hero Section */}
      <div style={{ background: 'linear-gradient(135deg, #2D2824 0%, #1E6B7B 100%)' }} className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-5xl mx-auto px-6 py-12 text-center">
          <motion.img 
            src={LOGO} alt="Newark SYEP" 
            className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white p-2 shadow-xl"
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
          />
          <motion.h1 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4"
            style={{ color: 'white' }}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          >
            Newark SYEP NextGEN
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl mb-2 font-medium"
            style={{ color: '#E86C3D' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            Your Digital Platform for Summer 2026
          </motion.p>
          <motion.p 
            className="text-base md:text-lg max-w-2xl mx-auto mb-8"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          >
            Track your growth. Explore careers. Earn points. Connect with your cohort. 
            Everything you need for your summer experience — in one place.
          </motion.p>
          
          <motion.div 
            className="flex flex-wrap gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          >
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-3 text-lg font-bold shadow-lg"
              data-testid="login-btn"
            >
              <LogIn className="w-5 h-5 mr-2" /> Log In Now
            </Button>
            <Button 
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="rounded-full px-8 py-3 text-lg font-bold border-white/30 text-white hover:bg-white/10"
            >
              See Features <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Action Banner */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-3 text-white">
          <Flame className="w-6 h-6" />
          <p className="font-bold text-center">
            Jump in now! Every activity earns you points toward the leaderboard.
          </p>
          <Flame className="w-6 h-6" />
        </div>
      </div>

      {/* Role Selector */}
      <div id="features" className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">I am a...</h2>
          <p className="text-gray-500">Select your role to see your features</p>
        </div>
        
        <Tabs value={activeRole} onValueChange={setActiveRole}>
          <TabsList className="bg-white rounded-2xl p-1.5 shadow-md mb-8 w-full max-w-lg mx-auto">
            <TabsTrigger value="youth" className="flex-1 rounded-xl py-3 text-sm font-bold data-[state=active]:bg-[#2D2824] data-[state=active]:text-white">
              NextGEN Youth
            </TabsTrigger>
            <TabsTrigger value="monitor" className="flex-1 rounded-xl py-3 text-sm font-bold data-[state=active]:bg-[#2D2824] data-[state=active]:text-white">
              Lead Monitor
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex-1 rounded-xl py-3 text-sm font-bold data-[state=active]:bg-[#2D2824] data-[state=active]:text-white">
              Admin
            </TabsTrigger>
          </TabsList>

          {/* ========== YOUTH FEATURES ========== */}
          <TabsContent value="youth">
            {/* Start Here - Top 3 */}
            <div className="mb-10">
              <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" /> Start Here — Do These First
              </h3>
              <p className="text-gray-500 text-sm mb-4">Get points immediately</p>
              
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: 'Post Your First Win', desc: 'Go to Wins Wall and tell everyone what you\'re proud of this summer. First time? +15 points.', icon: Trophy, color: '#F59E0B', points: 15, path: '/wins' },
                  { title: 'Ask the AI Career Coach', desc: 'Tell it your interests and it\'ll suggest careers you never thought of. It\'s like a career advisor that\'s always available.', icon: Brain, color: '#8B5CF6', points: 5, path: '/ai-guide' },
                  { title: 'Do Your Weekly Check-in', desc: '9 quick questions about your week. Your monitor reads every single one. Takes 3 minutes.', icon: CheckCircle, color: '#10B981', points: 20, path: '/checkin' },
                ].map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}>
                    <Card className="p-5 rounded-2xl bg-white hover:shadow-lg transition-all cursor-pointer h-full border-2 border-transparent hover:border-orange-200"
                      onClick={() => navigate(item.path)}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: item.color + '20' }}>
                        <item.icon className="w-6 h-6" style={{ color: item.color }} />
                      </div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{item.desc}</p>
                      <Badge className="bg-orange-100 text-orange-700 font-bold">+{item.points} pts</Badge>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* All Features Grid */}
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" /> All Your Features
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { title: 'Wins Wall', desc: 'Celebrate achievements, hype up your cohort', icon: Trophy, color: '#F59E0B', tag: 'SOCIAL' },
                { title: 'AI Career Guide', desc: 'AI-powered career counselor — ask anything', icon: Brain, color: '#8B5CF6', tag: 'AI' },
                { title: 'SMART Goals', desc: 'Set goals, track progress, earn points', icon: Target, color: '#10B981', tag: '25-50 PTS' },
                { title: 'Weekly Check-in', desc: 'Share your mood, highlights, challenges', icon: CheckCircle, color: '#3B82F6', tag: '20 PTS' },
                { title: '13 Workshop Courses', desc: 'Money Moves, Boss Mode, Real Talk + more', icon: BookOpen, color: '#EC4899', tag: 'XP' },
                { title: '6 Impact Projects', desc: 'Side Hustle, Personal Brand, Creator Launch', icon: FolderOpen, color: '#E86C3D', tag: 'BUILD' },
                { title: 'Skills Assessment', desc: '12 workplace scenarios — test your judgment', icon: Award, color: '#14B8A6', tag: '50 PTS' },
                { title: 'Career Explorer', desc: 'Save careers, compare salaries, plan your path', icon: MapPin, color: '#6366F1', tag: 'EXPLORE' },
                { title: 'Weekly Trivia', desc: '5 questions per week — test your knowledge', icon: Sparkles, color: '#F97316', tag: '25 PTS' },
                { title: 'Help Desk Q&A', desc: 'Ask your monitor anything — get answers fast', icon: HelpCircle, color: '#0EA5E9', tag: '5 PTS' },
                { title: 'Inbox Messages', desc: 'DM your lead monitor directly', icon: MessageSquare, color: '#64748B', tag: 'PRIVATE' },
                { title: 'Leaderboard', desc: 'See how you rank against all 92 NextGEN', icon: BarChart3, color: '#EF4444', tag: 'COMPETE' },
                { title: 'Badges', desc: '15+ badges across Bronze, Silver, Gold, Diamond', icon: Award, color: '#D97706', tag: 'COLLECT' },
                { title: 'Events', desc: 'View schedule, check in to sessions', icon: Calendar, color: '#7C3AED', tag: 'ATTEND' },
                { title: 'Resources', desc: 'Program docs, guides, handbooks', icon: FolderOpen, color: '#059669', tag: 'DOCS' },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white hover:shadow-sm transition-all">
                  <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: f.color + '15' }}>
                    <f.icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-sm text-gray-900">{f.title}</h4>
                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: f.color + '15', color: f.color }}>{f.tag}</Badge>
                    </div>
                    <p className="text-xs text-gray-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Points Table */}
            <Card className="p-5 rounded-2xl bg-white mt-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" /> Points Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {[
                  { activity: 'Weekly Check-in', pts: 20 },
                  { activity: 'Weekly Trivia', pts: 25 },
                  { activity: 'Complete Short Goal', pts: 25 },
                  { activity: 'Complete Long Goal', pts: 50 },
                  { activity: 'Post a Win', pts: 15 },
                  { activity: 'Skills Assessment', pts: 50 },
                  { activity: 'Ask a Question', pts: 5 },
                  { activity: 'Orientation Survey', pts: 15 },
                ].map((p, i) => (
                  <div key={i} className="flex justify-between p-2 rounded-lg bg-gray-50">
                    <span className="text-gray-700">{p.activity}</span>
                    <span className="font-bold text-orange-600">+{p.pts}</span>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* ========== MONITOR FEATURES ========== */}
          <TabsContent value="monitor">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" /> Your Lead Monitor Dashboard
              </h3>
              <p className="text-gray-500 text-sm mb-4">Everything you need to support your assigned youth</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'My Youth Overview', desc: 'See all your assigned youth with points, streaks, risk flags, and engagement status at a glance.', icon: Users, color: '#3B82F6' },
                { title: 'AI Youth Summaries', desc: 'Click any youth → AI generates a narrative about their strengths, growth areas, and coaching tips.', icon: Brain, color: '#8B5CF6', tag: 'AI POWERED' },
                { title: 'AI Coach Assistant', desc: 'For each youth: priority action, talking points, risk flags, celebration opportunities, and a check-in question.', icon: Sparkles, color: '#E86C3D', tag: 'AI POWERED' },
                { title: 'Answer Q&A Questions', desc: 'Youth ask you questions → you get instant email → respond in the app. Toggle "Share with Cohort" for answers everyone needs.', icon: MessageSquare, color: '#10B981' },
                { title: 'AI Suggested Responses', desc: 'When a youth asks a question, AI drafts a response for you to review, edit, and send.', icon: Brain, color: '#F59E0B', tag: 'AI POWERED' },
                { title: 'Review Weekly Check-ins', desc: 'Read each youth\'s mood, highlights, and challenges. Write feedback to show you\'re paying attention.', icon: CheckCircle, color: '#14B8A6' },
                { title: 'Activity Feed', desc: 'Real-time log of everything your youth do — goals set, wins posted, check-ins submitted.', icon: Zap, color: '#EC4899' },
                { title: 'Parent Messages', desc: 'Receive and reply to messages from parents of your assigned youth.', icon: MessageSquare, color: '#6366F1' },
                { title: 'Work Journal View', desc: 'See your youth\'s daily journal entries — their experiences, moods, and what they\'re learning.', icon: BookOpen, color: '#0EA5E9' },
                { title: 'Content Moderation', desc: 'Review Wins Wall posts from your youth and flag anything inappropriate.', icon: Award, color: '#EF4444' },
              ].map((f, i) => (
                <Card key={i} className="p-4 rounded-2xl bg-white hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: f.color + '15' }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-sm text-gray-900">{f.title}</h4>
                        {f.tag && <Badge className="bg-violet-100 text-violet-700 text-[9px]">{f.tag}</Badge>}
                      </div>
                      <p className="text-xs text-gray-600">{f.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200 mt-6">
              <h4 className="font-bold text-amber-900 text-sm mb-2">Email Notifications You'll Receive:</h4>
              <ul className="text-xs text-amber-800 space-y-1">
                <li>Instant email when a youth posts a question</li>
                <li>Daily 6pm reminder for unanswered questions (weekdays)</li>
                <li>Alert when a youth submits their check-in</li>
                <li>Alert when a parent sends you a message</li>
                <li>Weekly program digest (Monday morning, when enabled)</li>
              </ul>
            </Card>
          </TabsContent>

          {/* ========== ADMIN FEATURES ========== */}
          <TabsContent value="admin">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" /> Program Admin Dashboard
              </h3>
              <p className="text-gray-500 text-sm mb-4">Complete oversight of your program, youth, and staff</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'Program KPIs', desc: 'Total youth, goals, check-ins, wins, courses, points, consent status — all at a glance.', icon: BarChart3, color: '#E86C3D' },
                { title: 'AI Program Insights', desc: 'AI-generated executive narrative with engagement grade (A-F), key wins, and funder highlights.', icon: Brain, color: '#8B5CF6', tag: 'AI' },
                { title: '8 Visual Analytics Dashboards', desc: 'Interactive KPIs: Courses, Projects, Events, Q&A, Parents, Staff, Worksites, Wellness.', icon: BarChart3, color: '#3B82F6' },
                { title: 'Youth Deep-Dive', desc: 'Click any youth → see their COMPLETE activity: goals, check-ins, wins, projects, Q&A, everything.', icon: Users, color: '#10B981' },
                { title: 'PDF + CSV Reports', desc: '3 branded PDF reports + 7 CSV data exports. Ready for funders and presentations.', icon: FolderOpen, color: '#F59E0B' },
                { title: 'Staff Performance', desc: 'See each monitor\'s last login, Q&A response rate, assigned youth, check-in reviews.', icon: Users, color: '#EC4899' },
                { title: 'Wins Wall Overview', desc: 'See all youth celebrations, accomplishments, and engagement across the program.', icon: Trophy, color: '#D97706' },
                { title: 'Q&A Conversations', desc: 'View all questions from all youth, response rates, staff performance.', icon: MessageSquare, color: '#14B8A6' },
                { title: 'Weekly Digest', desc: 'One-click send engagement summary to all staff and parents.', icon: Zap, color: '#6366F1' },
                { title: 'Org & Program Settings', desc: 'Edit org name, phone, website, program details, dates, branding.', icon: Target, color: '#64748B' },
              ].map((f, i) => (
                <Card key={i} className="p-4 rounded-2xl bg-white hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: f.color + '15' }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-sm text-gray-900">{f.title}</h4>
                        {f.tag && <Badge className="bg-violet-100 text-violet-700 text-[9px]">{f.tag}</Badge>}
                      </div>
                      <p className="text-xs text-gray-600">{f.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* CTA Footer */}
      <div style={{ background: 'linear-gradient(135deg, #2D2824 0%, #1E6B7B 100%)' }} className="py-12 px-6 text-center">
        <h2 className="text-3xl font-extrabold mb-3" style={{ color: 'white' }}>Ready to Jump In?</h2>
        <p className="text-lg mb-6" style={{ color: 'rgba(255,255,255,0.8)' }}>
          Your summer. Your growth. Your platform.
        </p>
        <Button 
          onClick={() => navigate('/login')} 
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-4 text-lg font-bold shadow-xl"
        >
          <LogIn className="w-5 h-5 mr-2" /> Log In to YAS Connect
        </Button>
        <p className="text-sm mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
          yasconnect.co — Powered by YAS Connect
        </p>
      </div>
    </div>
  );
};

export default SYEPLaunchPage;
